import React from 'react';
import { Navigate, useLoaderData, useRevalidator } from 'react-router-dom';
import { ExclamationTriangleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { Await } from '@oysterjs/ui/async';
import { Button } from '@oysterjs/ui/button';
import { PageTitle } from '@oysterjs/ui/page';
import { Spinner } from '@oysterjs/ui/spinner';
import { TextLink } from '@oysterjs/ui/text';

import type {
  BulkDraft,
  BulkDraftEmail,
  BulkDraftPageData,
  SentBatch
} from '~/api/data/email-bulk-send/queries';
import {
  Action,
  EmailBulkSendAction,
  EmailBulkSendRecipientSendStatus,
  type MailboxAccountFieldsFragment,
  MailboxAccountStatus,
  Resource
} from '~/api/graphql';
import { Dash } from '~/components/clients/clients-columns';
import { InlineNotice } from '~/components/inline-notice';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import { BulkBadge } from '~/components/mailbox/bulk-badge';
import { OUTGOING_PATH } from '~/components/mailbox/bulk-send-paths';
import { type MovedToDrafts, MovedToDraftsToast } from '~/components/mailbox/moved-to-drafts-toast';
import { isBulkSendDrafting } from '~/components/mailbox/outbox-bulk-send-rows';
import { workspaceArtifactHref } from '~/components/mailbox/outbox-columns';
import { useCurrentAgencyUserId } from '~/components/current-user';
import { usePermissions } from '~/components/permissions';
import { pluralize } from '~/lib/pluralize';
import {
  BulkDraftActions,
  NOTHING_COMPOSED,
  type StartedBulkSend,
  bulkDraftActionsOffReason,
  useMailboxBulkSendUsage
} from './bulk-draft-actions';
import { BulkDraftAddRecipients } from './bulk-draft-add-recipients';
import { BulkDraftComposer } from './bulk-draft-composer';
import {
  type BulkDraftRecipient,
  type BulkDraftTemplates,
  bulkDraftRecipientStatus,
  bulkDraftTemplates,
  hasDraftedEmail,
  isReadyToSend,
  recipientLabel
} from './bulk-draft-recipient-status';
import { RemoveRecipientButton } from './bulk-draft-remove-recipient';
import { BulkDraftRecipients } from './bulk-draft-recipients';
import {
  BatchProgressLoadFailure,
  BulkSendRecipientStatusBadge,
  SentBatchSummary
} from './bulk-draft-sent';
import {
  BulkDraftReconnectNotice,
  BulkDraftSendFrom,
  defaultSendingMailboxId
} from './bulk-draft-send-from';
import { batchTally } from './outbox-bulk-send-summary';
import { OutboxScheduleBanners, mailboxReconnectBanner } from './outbox-schedule-banners';
import { useLiveBulkDraftPage } from './use-live-bulk-draft-page';

/** What sending a bulk draft asks of the server. */
const BULK_SEND_PERMISSIONS = [
  { resource: Resource.Mailbox, action: Action.Send },
  { resource: Resource.EmailTemplate, action: Action.Read }
];

type BulkDraftLoaderData = {
  data: Promise<{
    bulkDraftPage: BulkDraftPageData;
    mailboxAccounts: MailboxAccountFieldsFragment[];
  }>;
};

const BulkDraftTitle = (
  props: React.PropsWithChildren<{
    title: string | JSX.Element;
    description?: JSX.Element;
    menu?: JSX.Element;
    fillHeight?: boolean;
  }>
) => <PageTitle eyebrow="Back to Outgoing" eyebrowHref={OUTGOING_PATH} {...props} />;

const BulkDraftName = ({ name }: { name: string }) => (
  <span className="flex flex-wrap items-center gap-2">
    {name}
    <BulkBadge />
  </span>
);

/** "38 recipients · Template: Renewal reminder · Open workspace". */
const BulkDraftOverview = ({
  recipientCount,
  sharedTemplateName,
  workspaceHref
}: {
  recipientCount: number;
  sharedTemplateName: string | undefined;
  workspaceHref: string;
}) => (
  <>
    {recipientCount} {pluralize(recipientCount, 'recipient')} ·{' '}
    {sharedTemplateName && `Template: ${sharedTemplateName} · `}
    <TextLink href={workspaceHref}>Open workspace</TextLink>
  </>
);

const sharedTemplateNameOf = (
  templates: BulkDraftTemplates,
  templateNameById: Record<string, string>
) => (templates.kind === 'shared' ? templateNameById[templates.templateId] : undefined);

export const BulkDraftPage = () => {
  const loaderData = useLoaderData() as BulkDraftLoaderData;
  return (
    <React.Suspense
      fallback={
        <BulkDraftTitle title="Bulk draft">
          <LibraryListLoading title="Loading bulk draft…" />
        </BulkDraftTitle>
      }
    >
      <Await resolve={loaderData.data}>
        {({ bulkDraftPage, mailboxAccounts }) => (
          <LiveBulkDraft loadedPage={bulkDraftPage} mailboxAccounts={mailboxAccounts} />
        )}
      </Await>
    </React.Suspense>
  );
};

const LiveBulkDraft = ({
  loadedPage,
  mailboxAccounts
}: {
  loadedPage: BulkDraftPageData;
  mailboxAccounts: MailboxAccountFieldsFragment[];
}) => {
  const canSendInBulk = usePermissions().hasAllPermissions(BULK_SEND_PERMISSIONS);
  const {
    page,
    showBulkDraft,
    refreshAfterEdit,
    showSent,
    reloadBatch,
    reloadPage,
    removeRecipientNow,
    removeErrorById
  } = useLiveBulkDraftPage(loadedPage);
  const { bulkDraft, emailByArtifactId, templateNameById, batch } = page;
  const [movedToDrafts, setMovedToDrafts] = React.useState<MovedToDrafts>();
  // The sent page is a tree of its own, so its table opens where the draft's was scrolled to.
  const recipientsScrollTopRef = React.useRef(0);
  const showStarted = React.useCallback(
    async (started: StartedBulkSend) => {
      setMovedToDrafts(started.movedToDrafts);
      await showSent(started.sentAt);
    },
    [showSent]
  );
  if (!bulkDraft) {
    return <Navigate to={OUTGOING_PATH} replace />;
  }
  // A sent batch is shown to anyone who can see Outgoing; a draft only to those who can send it.
  if (!bulkDraft.sentAt && !canSendInBulk) {
    return (
      <BulkDraftTitle title="Bulk draft">
        <LibraryListEmptyState
          icon={LockClosedIcon}
          title="You don't have permission to send email from Cara."
          description="Bulk send uses the same permission as sending one email. Ask an admin if you need it."
        />
      </BulkDraftTitle>
    );
  }
  return (
    // From lg up, where the app has no top bar, the page is one screen tall and only the
    // recipients table scrolls. The negative margin takes back the main area's padding. On a
    // screen too short for the table's least height the page scrolls inside itself, so its
    // bottom padding stays under the table rather than the table running to the window's edge.
    <div className="lg:-my-12 lg:flex lg:h-screen lg:flex-col lg:overflow-y-auto lg:py-12">
      {bulkDraft.sentAt ? (
        <SentBulkDraftContent
          bulkDraft={bulkDraft}
          batch={batch}
          emailByArtifactId={emailByArtifactId}
          templateNameById={templateNameById}
          mailboxAccounts={mailboxAccounts}
          recipientsScrollTopRef={recipientsScrollTopRef}
          onReloadBatch={reloadBatch}
          onBatchChanged={reloadPage}
        />
      ) : (
        <BulkDraftContent
          bulkDraft={bulkDraft}
          emailByArtifactId={emailByArtifactId}
          templateNameById={templateNameById}
          mailboxAccounts={mailboxAccounts}
          recipientsScrollTopRef={recipientsScrollTopRef}
          onChange={showBulkDraft}
          onEmailEdited={refreshAfterEdit}
          onStarted={showStarted}
          onRemove={removeRecipientNow}
          removeErrorById={removeErrorById}
        />
      )}
      {movedToDrafts && <MovedToDraftsToast movedToDrafts={movedToDrafts} />}
    </div>
  );
};

const BulkDraftContent = ({
  bulkDraft,
  emailByArtifactId,
  templateNameById,
  mailboxAccounts,
  recipientsScrollTopRef,
  onChange,
  onEmailEdited,
  onStarted,
  onRemove,
  removeErrorById
}: {
  bulkDraft: BulkDraft;
  emailByArtifactId: Record<string, BulkDraftEmail>;
  templateNameById: Record<string, string>;
  mailboxAccounts: MailboxAccountFieldsFragment[];
  recipientsScrollTopRef: React.MutableRefObject<number>;
  /** Shows the bulk draft a change on the page answered with. */
  onChange: (bulkDraft: BulkDraft) => void;
  /** Reads the page again once the composer closes, for the subjects and held-back reasons. */
  onEmailEdited: () => void;
  onStarted: (started: StartedBulkSend) => Promise<void>;
  onRemove: (recipientId: string) => void;
  removeErrorById: ReadonlyMap<string, string>;
}) => {
  const recipientCount = bulkDraft.recipients.length;
  const { finished, total } = bulkDraft.draftProgress;
  const workspaceHref = `/app/workspaces/${bulkDraft.workspaceId}`;
  const templates = bulkDraftTemplates(bulkDraft.recipients, emailByArtifactId);
  const [mailboxAccountId, setMailboxAccountId] = React.useState(() =>
    defaultSendingMailboxId(mailboxAccounts)
  );
  // With mailboxes but none that can send, the one to reconnect is the agent's default.
  const mailboxToReconnect = mailboxAccountId
    ? undefined
    : (mailboxAccounts.find((account) => account.isDefault) ?? mailboxAccounts[0]);
  const sendingMailbox = mailboxAccounts.find((account) => account.id === mailboxAccountId);
  const isDrafting = isBulkSendDrafting(bulkDraft);
  const readyCount = bulkDraft.recipients.filter(isReadyToSend).length;
  // One read for both the limit line under Send from and Send all's daily limit.
  const usage = useMailboxBulkSendUsage(sendingMailbox?.id, readyCount);
  const hasNothingReady = !isDrafting && readyCount === 0;
  // The last recipient opened stays named while its slideout closes, so the title doesn't jump.
  const [editing, setEditing] = React.useState({ recipientId: '', isOpen: false });
  // Looked up among every recipient, so a redraft Cara starts doesn't close the composer.
  const editRecipient = bulkDraft.recipients.find(({ id }) => id === editing.recipientId);
  const numberOf = (recipientId: string) =>
    bulkDraft.recipients.findIndex(({ id }) => id === recipientId) + 1;
  const rowActions = React.useCallback(
    (recipient: BulkDraftRecipient) => (
      <span className="inline-flex items-center gap-2">
        {hasDraftedEmail(recipient) && (
          <Button
            color="white"
            aria-label={`Open ${recipientLabel(recipient)}`}
            onClick={() => setEditing({ recipientId: recipient.id, isOpen: true })}
          >
            Open
          </Button>
        )}
        {/* Only Cara drafts it, when asked in the workspace. */}
        {bulkDraftRecipientStatus(recipient) === 'notDrafted' && (
          <Button
            color="white"
            href={workspaceHref}
            title="Ask Cara to draft it"
            aria-label={`Open ${recipientLabel(recipient)}`}
          >
            Open
          </Button>
        )}
        <RemoveRecipientButton
          recipient={recipient}
          error={removeErrorById.get(recipient.id)}
          onRemove={() => onRemove(recipient.id)}
        />
      </span>
    ),
    [workspaceHref, onRemove, removeErrorById]
  );
  const actionsOffReason = bulkDraftActionsOffReason({
    isDrafting,
    hasMailbox: mailboxAccounts.length > 0,
    sendingMailbox,
    emailCount: readyCount
  });
  return (
    <BulkDraftTitle
      fillHeight
      title={<BulkDraftName name={bulkDraft.name} />}
      description={
        <>
          <BulkDraftOverview
            recipientCount={recipientCount}
            sharedTemplateName={sharedTemplateNameOf(templates, templateNameById)}
            workspaceHref={workspaceHref}
          />
          {isDrafting && (
            <span
              role="status"
              className="mt-2 flex items-center gap-2 text-sm font-medium text-sky-700 dark:text-sky-400"
            >
              <Spinner size="size-3.5" color="sky" />
              Drafting {finished} of {total}
            </span>
          )}
        </>
      }
      menu={
        <BulkDraftActions
          bulkSendId={bulkDraft.id}
          sendingMailbox={sendingMailbox}
          recipientCount={recipientCount}
          readyCount={readyCount}
          offReason={actionsOffReason}
          usage={usage}
          onStarted={onStarted}
        />
      }
    >
      <BulkDraftSendFrom
        mailboxAccounts={mailboxAccounts}
        mailboxAccountId={mailboxAccountId}
        usage={usage}
        onChange={setMailboxAccountId}
      />
      {mailboxToReconnect && <BulkDraftReconnectNotice account={mailboxToReconnect} />}
      {hasNothingReady && (
        <InlineNotice
          tone="warning"
          className="mb-4"
          title={NOTHING_COMPOSED}
          description="Open the emails with issues and fill in their blanks."
        />
      )}
      <BulkDraftAddRecipients bulkDraft={bulkDraft} onChange={onChange} />
      <BulkDraftRecipients
        bulkDraft={bulkDraft}
        emailByArtifactId={emailByArtifactId}
        templateNameById={templates.kind === 'mixed' ? templateNameById : null}
        rowActions={rowActions}
        scrollTopRef={recipientsScrollTopRef}
      />
      <BulkDraftComposer
        workspaceId={bulkDraft.workspaceId}
        artifactId={editing.isOpen ? (editRecipient?.artifactId ?? null) : null}
        title={`Email ${numberOf(editing.recipientId)} of ${recipientCount}`}
        fromAddress={sendingMailbox?.identity.email ?? 'No mailbox connected'}
        onClose={() => {
          setEditing((last) => ({ ...last, isOpen: false }));
          onEmailEdited();
        }}
      />
    </BulkDraftTitle>
  );
};

/**
 * Asks to reconnect the batch's mailbox while emails it still has to send wait, as Outgoing does
 * for any scheduled email. Move all to drafts sends nothing, so it isn't asked.
 */
const batchReconnectBanners = (
  batch: SentBatch | null,
  mailboxAccounts: MailboxAccountFieldsFragment[]
) => {
  if (!batch || batch.action === EmailBulkSendAction.MoveToDrafts) {
    return [];
  }
  const mailbox = mailboxAccounts.find((account) => account.id === batch.mailboxAccountId);
  const waitingCount = batchTally(batch).waiting;
  if (mailbox?.status !== MailboxAccountStatus.NeedsReconnect || waitingCount === 0) {
    return [];
  }
  const firstSendAt = batch.firstSendAt ? new Date(batch.firstSendAt) : undefined;
  const isFirstSendAhead = !!firstSendAt && firstSendAt.getTime() > Date.now();
  return [
    mailboxReconnectBanner(mailbox, waitingCount, isFirstSendAhead ? firstSendAt : undefined)
  ];
};

/**
 * Send the rest of a sent batch, for the person who sent it, while it has emails that never went
 * out and its round has nothing left to send; undefined otherwise. It sends from the batch's own
 * mailbox, under its daily limit.
 */
const useSendRestAction = (
  batch: SentBatch | null,
  mailboxAccounts: MailboxAccountFieldsFragment[],
  onSent: () => void
): React.ReactNode => {
  const currentUserId = useCurrentAgencyUserId();
  const restCount = batch?.restCount ?? 0;
  const isOffered = !!batch?.sentBy && batch.sentBy.id === currentUserId && restCount > 0;
  const sendingMailbox = mailboxAccounts.find(
    (account) =>
      account.id === batch?.mailboxAccountId && account.status === MailboxAccountStatus.Connected
  );
  const usage = useMailboxBulkSendUsage(isOffered ? sendingMailbox?.id : undefined, restCount);
  if (!batch || !isOffered) {
    return undefined;
  }
  return (
    <BulkDraftActions
      scope="rest"
      bulkSendId={batch.id}
      sendingMailbox={sendingMailbox}
      recipientCount={restCount}
      readyCount={restCount}
      offReason={bulkDraftActionsOffReason({
        isDrafting: false,
        hasMailbox: true,
        sendingMailbox,
        emailCount: restCount
      })}
      usage={usage}
      onStarted={async () => onSent()}
    />
  );
};

/**
 * A sent bulk draft, read-only: how far its batch has got, and where each recipient's email
 * stands.
 */
const SentBulkDraftContent = ({
  bulkDraft,
  batch,
  emailByArtifactId,
  templateNameById,
  mailboxAccounts,
  recipientsScrollTopRef,
  onReloadBatch,
  onBatchChanged
}: {
  bulkDraft: BulkDraft;
  batch: SentBatch | null;
  emailByArtifactId: Record<string, BulkDraftEmail>;
  templateNameById: Record<string, string>;
  mailboxAccounts: MailboxAccountFieldsFragment[];
  recipientsScrollTopRef: React.MutableRefObject<number>;
  onReloadBatch: () => void;
  /**
   * Reads the page again once the batch is cancelled, which can make it a draft again, moved,
   * or sent the rest of.
   */
  onBatchChanged: () => void;
}) => {
  const workspaceHref = `/app/workspaces/${bulkDraft.workspaceId}`;
  const templates = bulkDraftTemplates(bulkDraft.recipients, emailByArtifactId);
  const mailboxProviders = React.useMemo(
    () => new Map(mailboxAccounts.map((account) => [account.id, account.provider])),
    [mailboxAccounts]
  );
  const sendRest = useSendRestAction(batch, mailboxAccounts, onBatchChanged);
  const sentRecipientById = React.useMemo(
    () => new Map((batch?.recipients ?? []).map((recipient) => [recipient.id, recipient])),
    [batch]
  );
  const sentStatus = React.useCallback(
    (recipient: BulkDraftRecipient) => {
      const sentRecipient = sentRecipientById.get(recipient.id);
      return batch && sentRecipient ? (
        <BulkSendRecipientStatusBadge recipient={sentRecipient} batch={batch} />
      ) : (
        <Dash />
      );
    },
    [sentRecipientById, batch]
  );
  // Matches the summary's held-back count, so recipients not sent aren't counted in it.
  const isHeldBack = React.useCallback(
    (recipient: BulkDraftRecipient) =>
      sentRecipientById.get(recipient.id)?.sendStatus === EmailBulkSendRecipientSendStatus.HeldBack,
    [sentRecipientById]
  );
  const rowActions = React.useCallback(
    (recipient: BulkDraftRecipient) => {
      const email = emailByArtifactId[recipient.artifactId ?? ''];
      const sendStatus = sentRecipientById.get(recipient.id)?.sendStatus;
      // A recipient with no send status has no email of the batch to open.
      const isOpenable = !!sendStatus;
      // A held-back email opens in the composer, where it can still be sent.
      const isReadOnly = sendStatus !== EmailBulkSendRecipientSendStatus.HeldBack;
      return isOpenable && recipient.artifactId && email ? (
        <Button
          color="white"
          aria-label={`Open ${recipientLabel(recipient)}`}
          title={isReadOnly ? 'Opens this email, read-only.' : undefined}
          href={workspaceArtifactHref(bulkDraft.workspaceId, recipient.artifactId, email.version)}
        >
          Open
        </Button>
      ) : null;
    },
    [emailByArtifactId, sentRecipientById, bulkDraft.workspaceId]
  );
  return (
    <BulkDraftTitle
      fillHeight
      title={<BulkDraftName name={bulkDraft.name} />}
      description={
        <BulkDraftOverview
          recipientCount={bulkDraft.recipients.length}
          sharedTemplateName={sharedTemplateNameOf(templates, templateNameById)}
          workspaceHref={workspaceHref}
        />
      }
    >
      {batch ? (
        // Pulled up under the header, as the page title's own gap is wider than the summary's.
        <div className="-mt-5 mb-5">
          <SentBatchSummary
            batch={batch}
            mailboxProviders={mailboxProviders}
            sendRest={sendRest}
            onChanged={onBatchChanged}
          />
        </div>
      ) : (
        <BatchProgressLoadFailure className="mb-4" onReload={onReloadBatch} />
      )}
      <OutboxScheduleBanners banners={batchReconnectBanners(batch, mailboxAccounts)} />
      <BulkDraftRecipients
        bulkDraft={bulkDraft}
        emailByArtifactId={emailByArtifactId}
        templateNameById={templates.kind === 'mixed' ? templateNameById : null}
        rowActions={rowActions}
        sentStatus={sentStatus}
        isHeldBack={isHeldBack}
        scrollTopRef={recipientsScrollTopRef}
      />
    </BulkDraftTitle>
  );
};

export const BulkDraftLoadError = () => {
  const revalidator = useRevalidator();
  return (
    <BulkDraftTitle title="Bulk draft">
      <LibraryListEmptyState
        icon={ExclamationTriangleIcon}
        title="We couldn't load this bulk draft."
        description="Check your connection and try again."
        action={
          <Button
            color="white"
            loading={revalidator.state === 'loading'}
            onClick={() => revalidator.revalidate()}
          >
            Try again
          </Button>
        }
      />
    </BulkDraftTitle>
  );
};
